"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { ArrowRight, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Faqaccordion() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const faqs = [{
    question: "What are your standard turnaround times for custom labels?",
    answer: "We prioritize efficiency without compromising quality. Most standard orders are processed and shipped within 3-5 business days. For complex submittal packages or large-scale industrial projects, we provide a specific timeline upon order confirmation."
  }, {
    question: "Do you meet UL 508A certification requirements?",
    answer: "Yes. We specialize in providing compliant identification solutions for panel builders. Our materials and engraving processes are designed to meet strict industry standards, ensuring your equipment meets all necessary safety and regulatory certifications."
  }, {
    question: "Can you handle high-volume industrial engraving projects?",
    answer: "Absolutely. Our facility is equipped with advanced laser and mechanical engraving technology capable of handling large-scale production runs. Whether you need thousands of identical tags or serialized parts, we maintain precision and consistency throughout."
  }, {
    question: "What materials do you offer for harsh environments?",
    answer: "We stock a wide range of industrial-grade materials, including UV-stable phenolic, stainless steel, and high-performance acrylics. These materials are specifically selected for their resistance to chemicals, extreme temperatures, and outdoor exposure."
  }, {
    question: "Do you provide support for submittal packages?",
    answer: "Yes. We assist specifying engineers and contractors by providing comprehensive submittal packages, including material data sheets, layout proofs, and samples to ensure every project requirement is met before production begins."
  }, {
    question: "What is the difference between laser and mechanical engraving?",
    answer: "Laser engraving offers high-speed, intricate detail perfect for logos and fine text on metals and plastics. Mechanical engraving provides a deeper, more durable mark that is ideal for heavy-duty industrial tags that require long-term readability in rugged conditions."
  }, {
    question: "Can you engrave directly onto my provided parts?",
    answer: "Yes. Our Direct Part Marking (DPM) services allow us to engrave serial numbers, barcodes, and branding directly onto your components, providing a permanent, tamper-resistant identification solution."
  }, {
    question: "How do I submit my design files?",
    answer: "We accept most standard vector formats, including DXF, DWG, AI, and PDF. If you don't have a digital file, our design team can work from your sketches or specifications to create the necessary production files."
  }, {
    question: "Do you offer custom color-coding for arc flash labels?",
    answer: "Yes. We produce fully compliant arc flash labels that adhere to NFPA 70E standards. We can customize colors, warning headers, and specific hazard information to ensure your facility remains safe and compliant."
  }, {
    question: "How can I get a quote for my project?",
    answer: "You can request a quote through our website by uploading your project details or by contacting our sales team directly. We provide detailed, transparent pricing based on material, quantity, and complexity."
  }];
  return <section data-section-id="701" id="faqaccordion" className="bg-background py-20 md:py-32">
      <div className="container mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-24">
              <div ref={ref} className={`transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
                <span className="inline-block text-sm font-semibold uppercase tracking-widest text-primary mb-6">
                  FAQ
                </span>
                <h2 className="font-default text-4xl md:text-5xl lg:text-[56px] font-bold leading-[1.05] tracking-tight text-accent mb-6">
                  Expert answers for your projects.
                </h2>
                <p className="text-lg text-muted-foreground leading-relaxed mb-10 max-w-md">
                  Everything you need to know about our industrial engraving, material specifications, and production capabilities.
                </p>

                {}
                <div className="bg-card rounded-lg p-6 shadow-md border border-border mb-6">
                  <div className="flex items-start gap-4">
                    <SafeHydrate><Avatar className="size-12 shrink-0">
                      <AvatarImage src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/engraving_expert_avatar.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/engraving_expert_avatar.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/engraving_expert_avatar.png-wvc-sizes" alt="Industrial engraving support team" />
                      <AvatarFallback className="bg-secondary text-accent font-semibold">
                        IE
                      </AvatarFallback>
                    </Avatar></SafeHydrate>
                    <div className="flex-1">
                      <p className="font-default font-semibold text-accent mb-1">
                        Need technical assistance?
                      </p>
                      <p className="text-sm text-muted-foreground mb-3">
                        Our specialists are ready to help with your custom specifications.
                      </p>
                      <Link to="/ContactPage" className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-accent transition-colors group">
                        Contact our specialists
                        <ArrowRight className="size-4 text-primary transition-transform group-hover:translate-x-1" />
                      </Link>
                    </div>
                  </div>
                </div>

                {}
                <Button asChild size="lg" variant="outline" className="border-border text-accent hover:bg-primary hover:text-primary-foreground">
                  
                </Button>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, i) => <AccordionItem key={i} data-index={i} value={`item-${i}`} className="border-b border-border last:border-b-0 group/item data-[state=open]:border-l-[3px] data-[state=open]:border-l-primary data-[state=open]:pl-6 transition-all duration-300">
                  <AccordionTrigger className="py-6 hover:no-underline [&>svg]:hidden group">
                    <span className="font-default text-lg md:text-xl font-semibold text-accent text-left pr-6 leading-snug">
                      {faq.question}
                    </span>
                    <span aria-hidden="true" className="relative flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-primary transition-all duration-300 group-hover:bg-primary group-hover:text-primary-foreground group-data-[state=open]:bg-primary group-data-[state=open]:text-primary-foreground">
                      <span className="absolute h-0.5 w-4 bg-current rounded" />
                      <span className="absolute h-0.5 w-4 bg-current rounded transition-transform duration-300 group-data-[state=open]:rotate-90 group-data-[state=closed]:rotate-0 rotate-90" />
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 pr-12">
                    <p className="text-base text-muted-foreground leading-[1.7]">
                      {faq.answer}
                    </p>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}